import { Button, Heading, Text, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { Cohort, PaymentMethodType } from '@afterpaytouch/portal-api'
import { useTranslation, useSubHeadingColor } from '../../utils/convergence'
import React, { useEffect, useState } from 'react'
import { DisabledModal, Head, ModuleContainer, NewPaymentMethod } from '../../../src/component'
import { useDefaultNewPaymentMethodSelection } from '../../../src/component/NewPaymentMethod/NewPaymentMethod'
import { Route, useSummaryRoute } from '../../../src/config/router'
import { checkoutErrors, CheckoutPage, TopazError, ExpandableComponent } from '../../../src/model'
import { PageNames, PaymentMethodTypes, TrackingEvent } from '../../../src/model/monitoring'
import {
  resetApplePayError,
  resetCheckoutError,
  resetPreferredPaymentMethodErrors,
  resetRecoverableFlowError,
  setCheckoutError,
  setExpandedComponent,
  setHasUserInitiatedChangedPaymentMethod,
  useAppDispatch,
  useAppSelector,
  useExpandedComponentId,
  useAppRouter,
  useCardAddForCashCohort,
  useCohort,
  useExperiment,
  useIsOverduePaymentsFlow,
  useIsRecoverableFlow,
  useSelectedPaymentMethod,
} from '../../../src/state'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'

export const NewPaymentMethodPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const paymentMethod = useSelectedPaymentMethod()
  const { instrument } = useMonitoringWithEnduringEventProperties({
    paymentMethodType: PaymentMethodTypes[paymentMethod?.type as PaymentMethodType],
    pageName: PageNames.ADD_NEW_PAYMENT_METHOD,
  })
  const { headingSize, textSize, smallTextSize } = useCheckoutSizes()
  const summaryRoute = useSummaryRoute()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isOverduePaymentsFlow = useIsOverduePaymentsFlow()

  const { control: isCardHolderNameField } = useExperiment('CheckoutHideCardHolderName')
  const addCardText = isCardHolderNameField ? t('paymentMethod:addACard') : t('paymentMethod:addACardExperiment')
  const pageTitle = `${t('paymentMethod:addNewPaymentMethod')} | ${t('common:brandName')}`
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const subHeadingColor = useSubHeadingColor()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const isCardAddEnabled = useCardAddForCashCohort()
  const blockCashCohortPaymentMethodAdd = isCashOnlyCohort && (isRecoverableFlow || isOverduePaymentsFlow)
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'applePay'>(useDefaultNewPaymentMethodSelection())
  const expandedComponentId = useAppSelector(useExpandedComponentId)

  const handleSubmit = async (): Promise<void> => {
    if (isRecoverableFlow) {
      dispatch(resetRecoverableFlowError())
    } else {
      dispatch(resetCheckoutError())
    }

    dispatch(resetApplePayError())
    dispatch(resetPreferredPaymentMethodErrors())
    dispatch(setHasUserInitiatedChangedPaymentMethod())
    if (expandedComponentId === ExpandableComponent.PAYMENT_METHOD_INLINE) {
      dispatch(setExpandedComponent(null))
    }

    router.push(summaryRoute)
  }

  const handleTerminalError = (reason: TopazError): void => {
    dispatch(
      setCheckoutError({
        error: checkoutErrors[reason],
        errorCode: reason,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const saveAddNewPaymentMethod = instrument(TrackingEvent.PRESSED_ADD_NEW_PAYMENT_METHOD_SAVE_BUTTON, () => setFormSubmitted(!formSubmitted))

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.PAYMENT_METHOD)
  }, [])

  useEffect(() => {
    if (blockCashCohortPaymentMethodAdd) {
      router.replace(summaryRoute)
    }
  }, [blockCashCohortPaymentMethodAdd, router, summaryRoute])

  const renderSubtext = (): React.ReactNode => {
    const textContent = (
      <Text size={textSize} color={subHeadingColor}>
        {addCardText}
      </Text>
    )

    return isArcadeDesignLanguage ? (
      <ModuleContainer showBorder={false} padding='None' noBottomMargin>
        {textContent}
      </ModuleContainer>
    ) : (
      textContent
    )
  }

  if (blockCashCohortPaymentMethodAdd) {
    return null
  }

  return (
    <>
      <Head title={pageTitle} />
      {!isArcadeDesignLanguage && <Heading size={headingSize}>{t('paymentMethod:addNewPaymentMethod')}</Heading>}
      {!isArcadeDesignLanguage && renderSubtext()}
      <ModuleContainer padding={{ vertical: 'Small' }} mintBorder showBorder={!isArcadeDesignLanguage}>
        <NewPaymentMethod
          onSuccess={async () => await handleSubmit()}
          onSubmit={(isSubmitting) => setIsSubmitting(isSubmitting)}
          formSubmitted={formSubmitted}
          onTerminalError={handleTerminalError}
          displayCreditCardMessage={false}
          onPaymentMethodChange={setSelectedMethod}
        />
      </ModuleContainer>
      {isCashOnlyCohort && isCardAddEnabled && selectedMethod === 'card' && (
        <ModuleContainer showBorder={false} padding='None'>
          <Text size={smallTextSize} color={subHeadingColor}>
            {t('paymentMethod:cashCohortCardDisclosure')}
          </Text>
        </ModuleContainer>
      )}
      <Button loading={isSubmitting} padding='Fluid' disabled={isSubmitting} testNameSpace='new-payment-method' onClick={saveAddNewPaymentMethod}>
        {isArcadeDesignLanguage ? t('paymentMethod:savePaymentMethod') : t('paymentMethod:save')}
      </Button>
      <TrackPageViewed />
      {isSubmitting && <DisabledModal />}
    </>
  )
}
